import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useEffect, useState } from "react";
import { Colors, Text, View } from "react-native-ui-lib";

import { CheckoutSummaryRootParamList } from "../../../../types";
import { SaleState } from "../../../../utilities/data/model/Sale";
import { useLocalSettingsProvider } from "../../../../utilities/local-settings";
import { moneyToFormattedString } from "../../../../utilities/money";
import { useReceiptsController } from "../../../../utilities/receipt";
import { OnPrintReceiptOptions } from "../../../../utilities/receipt/types";
import { usePrintReceipt } from "../../../../utilities/receipt/use-print-receipt";
import { useCountDown } from "../../../../utilities/use-count-down";
import { CustomerCard } from "../../components/CustomerCard";
import { SalePaymentSummary } from "../../components/SalePaymentSummary";
import { useCheckoutContextProvider } from "../../utils/checkout";
import { useCloseSale } from "../../utils/sale-controller";

import { AntDesign } from "@expo/vector-icons";
import {
  Banner,
  Card,
  Page,
  Stack,
  Button,
  useThemeProvider,
  TileButtonActionList,
} from "@towersystems/roam-common-native";
import { SaleSaleItemsSummary } from "../../components/SaleSaleItemsSummary";
import { SaleTotalsSummary } from "../../components/SaleTotalsSummary";
import { usePrintDocket } from "../../../../utilities/docket";

export type CheckoutReviewScreenProps = NativeStackScreenProps<
  CheckoutSummaryRootParamList,
  "CheckoutReviewScreen"
>;

export const CheckoutReviewScreen = ({
  navigation,
}: CheckoutReviewScreenProps) => {
  const {
    settings: { automaticallyPrintReceipts, automaticallyCompleteSale },
  } = useLocalSettingsProvider();
  const [submitting, setSubmitting] = useState(false);
  const { terminal, sale, cashManagedPaymentMethod } =
    useCheckoutContextProvider();
  const { colors } = useThemeProvider();

  const noReceiptTemplate = !terminal.defaultReceiptTemplate.id;

  const {
    countdown,
    clear: clearCountdown,
    running,
  } = useCountDown({
    callback: () => {
      if (automaticallyCompleteSale) {
        handleOnCloseSale();
      }
    },
  });

  useEffect(() => {
    navigation.setOptions({
      headerTitle: () => {
        return (
          <View style={{ alignItems: "center" }}>
            <Text style={{ fontSize: 22 }}>{`${sale.saleType.toUpperCase()}#${
              sale.invoiceNumber
            }`}</Text>
          </View>
        );
      },
    });
  }, []);

  const { handleOnPrintReceipt } = usePrintReceipt({
    controller: useReceiptsController(),
  });

  const { handleOnPrintAllAutomaticDockets } = usePrintDocket();

  const closeSale = useCloseSale();

  // this is temporary, we need to figure out how to get the change due

  const isCustomerSaleOrLayby = [
    SaleState.LAYBY_OPEN,
    SaleState.LAYBY_CLOSED,
  ].includes(sale.saleState);

  const isCustomerInvoice = [
    SaleState.INVOICE_OPEN,
    SaleState.INVOICE_CLOSED,
  ].includes(sale.saleState);

  const maybeChange =
    sale.isSale || sale.isProforma
      ? sale.payments[sale.payments.length - 1]
      : undefined;

  const saleChangeTitle =
    (sale.isSale || sale.isProforma) && maybeChange && maybeChange.amount < 0
      ? moneyToFormattedString(
          Math.abs(
            (maybeChange &&
              maybeChange.paymentMethodId == cashManagedPaymentMethod.id &&
              (maybeChange?.amount < 0 ? maybeChange?.amount : 0)) ||
              0
          )
        )
      : undefined;

  let title = saleChangeTitle
    ? `Change Due: ${saleChangeTitle}`
    : "Sale Completed";

  if (sale.isLayby) {
    title = "Layby Started";
  } else if (sale.isInvoice) {
    title = "Customer Invoice Created";
  } else if (sale.isQuote) {
    title = "Quote Created";
  }

  const onPrintReceiptPressed = async (options?: OnPrintReceiptOptions) => {
    try {
      const defaultPrinter = await terminal.defaultReceiptTemplate.fetch();
      if (defaultPrinter) {
        await handleOnPrintReceipt(sale, defaultPrinter, options);
      }
    } catch (e: any) {
      console.log("FAILED TO PRINT RECEIPT: ", e.message);
      throw e;
    }
  };

  const handleOnCloseSale = async () => {
    setSubmitting(true);

    if (automaticallyPrintReceipts && noReceiptTemplate == false) {
      await onPrintReceiptPressed({
        afterPrint: async () => {
          try {
            console.log("FINDME: handleOnPrintAllAutomaticDockets");
            await handleOnPrintAllAutomaticDockets(sale);
            console.log("FINDME: handleOnPrintAllAutomaticDockets done");
          } catch (err) {
            // dont let the error stop the sale from closing
            console.log(err);
          }

          return closeSale()
            .then((i) => navigation.pop())
            .catch((e) => console.log(e));
        },
      }).catch(async (err) => {
        return closeSale()
          .then((i) => navigation.pop())
          .catch((e) => console.log(e))
          .finally(() => setSubmitting(false));
      });
    } else {
      try {
        await handleOnPrintAllAutomaticDockets(sale);
      } catch (err) {
        console.log(err);
      }
      return closeSale()
        .then((i) => navigation.pop())
        .catch((e) => console.log(e))
        .finally(() => setSubmitting(false));
    }
  };

  const footerMarkup = (
    <View>
      <Button
        disabled={submitting}
        fullWidth
        style={{}}
        onPress={handleOnCloseSale}
        backgroundColor={Colors.primary}
      >
        <Text style={{ color: Colors.white, fontSize: 22, fontWeight: "bold" }}>
          {automaticallyCompleteSale && running
            ? `Next Transaction (${countdown})`
            : "Next Transaction"}
        </Text>
      </Button>
    </View>
  );

  const receiptActions = [
    {
      id: 1,
      label: "Print Receipt",
      isAction: true,
      onPress: () => {
        clearCountdown();
        onPrintReceiptPressed().catch((e) => console.log(e));
      },
    },
    {
      id: 20,
      label: sale.receiptTo ? "Cancel receipt to" : "Email Receipt",
      isAction: true,
      isDestructive: Boolean(sale.receiptTo),
      children: sale?.receiptTo ? (
        <View>
          <Text>{sale.receiptTo}</Text>
        </View>
      ) : undefined,
      onPress: () => {
        clearCountdown();
        if (sale?.receiptTo) {
          return sale.updateReceiptTo(null);
        } else {
          navigation.navigate("CheckoutEmailReceiptScreen", {});
        }
      },
    },
  ];

  const handleOnEditCustomer = async () => {
    return sale?.customer
      .fetch()
      .then((i) => {
        //@ts-ignore
        navigation.navigate("ActionsRootScreen", {
          screen: "CustomerRootScreen",
          params: {
            screen: "EditCustomerScreen",
            params: {
              customerId: i.id,
            },
          },
        });
      })
      .catch((e) => console.log(e));
  };

  const noDefaultPrinterWarning = noReceiptTemplate ? (
    <View>
      <Banner
        title="WARNING:"
        status="warning"
        icon={
          <AntDesign
            name="exclamationcircle"
            size={20}
            color={colors.borderWarning}
          />
        }
      >
        <Text>
          {`Terminal has no receipt template set. Please set a default receipt template in the settings.`}
        </Text>
      </Banner>
    </View>
  ) : undefined;

  const actions = [];

  if (!noDefaultPrinterWarning) {
    actions.push(...receiptActions);
  }

  if (!sale.customer.id) {
    actions.push({
      id: 2,
      label: "Add customer",
      isAction: true,
      onPress: () => {
        navigation.navigate("CustomerRootScreen", {
          screen: "CustomerListScreen",
        });
      },
    });
  }

  actions.push({
    id: "more-actions",
    label: "More actions",
    isAction: true,
    onPress: () => {
      navigation.navigate("CheckoutSummaryMoreActionsRootScreen", {
        screen: "CheckoutSummaryMoreActionsScreen",
      });
    },
  });

  return (
    <Page
      titleContainerStyle={{ alignItems: "center" }}
      title={title}
      footer={footerMarkup}
    >
      <Stack vertical>
        {noDefaultPrinterWarning}
        {actions.length >= 1 ? (
          <View>
            <TileButtonActionList actions={actions} />
          </View>
        ) : null}
        <View>
          {sale?.customer.id ? (
            <CustomerCard
              //@ts-ignore
              customerId={sale.customer.id}
              onEditCustomer={
                !isCustomerSaleOrLayby && !isCustomerInvoice
                  ? handleOnEditCustomer
                  : undefined
              }
            />
          ) : undefined}
        </View>
        {sale ? (
          <>
            <Card>
              <SaleTotalsSummary sale={sale} />
            </Card>
            <Card>
              <SaleSaleItemsSummary sale={sale} />
            </Card>
            {sale.payments.length > 0 ? (
              <Card title="Payments">
                <SalePaymentSummary sale={sale} />
              </Card>
            ) : undefined}
          </>
        ) : undefined}
      </Stack>
    </Page>
  );
};
